import { useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

import useCheckout from '@/features/Checkout/hooks/useCheckout'
import FieldCheckbox from '@/ui/FieldCheckbox'
import InputQty from '@/ui/InputQty'
import LazyImage from '@/ui/LazyImage'

import { CURRENCY } from '../../../../lib/consts'
import Button from '../../../../ui/Button'
import Modal from '../../../../ui/Modal'
import {
  ProductAddonBody,
  ProductAddonBorder,
  ProductAddonCaption,
  ProductAddonCheckbox,
  ProductAddonOutline,
  ProductAddonPrice,
  ProductAddonTitle,
  ProductAddonWrapper,
  ProductModalAddonsHeader,
  ProductModalAddonsList,
  ProductModalAddonsScroll,
  ProductModalAddonsTitle,
  ProductModalAddonsWrapper,
  ProductModalFooter,
  ProductModalFooterTotal,
  ProductModalMain,
  ProductModalMainContent,
  ProductModalMainContentCaption,
  ProductModalMainContentTitle,
  ProductModalMainImage,
  ProductModalVariants,
  ProductVariantCaption,
  ProductVariantContent,
  ProductVariantContentBody,
  ProductVariantContentCheckbox,
  ProductVariantImage,
  ProductVariantPrice,
  ProductVariantTitle,
  ProductVariantWrapper
} from './ProductModal.styles'

const ProductVariant = ({ name, caption, price, active, onChange }) => {
  return (
    <ProductVariantWrapper onClick={() => onChange(!active)} active={active}>
      <ProductVariantImage src={require('@/assets/img/product-default.jpg')} />
      <ProductVariantContent>
        <ProductVariantContentCheckbox>
          <FieldCheckbox checked={active} />
        </ProductVariantContentCheckbox>
        <ProductVariantContentBody>
          <ProductVariantTitle>{name}</ProductVariantTitle>
          <ProductVariantCaption>{caption}</ProductVariantCaption>
          <ProductVariantPrice>
            {price.amount.toFixed(2)} {price.currency}
          </ProductVariantPrice>
        </ProductVariantContentBody>
      </ProductVariantContent>
    </ProductVariantWrapper>
  )
}

const ProductAddon = ({ name, additionalInfo, price, active, onChange }) => {
  return (
    <ProductAddonWrapper onClick={() => onChange(!active)} active={active}>
      <ProductAddonBorder />
      <ProductAddonOutline />
      <ProductAddonCheckbox>
        <FieldCheckbox checked={active} size="sm" />
      </ProductAddonCheckbox>
      <ProductAddonBody>
        <ProductAddonTitle>{name}</ProductAddonTitle>
        {additionalInfo && <ProductAddonCaption>{additionalInfo}</ProductAddonCaption>}
      </ProductAddonBody>
      <ProductAddonPrice>
        {price.amount.toFixed(2)}
        {price.currency}
      </ProductAddonPrice>
    </ProductAddonWrapper>
  )
}

const ProductModal = ({ title, onClose, product, t, mode = 'ADD' }) => {
  const { addToCart, cart, updateCartItem } = useCheckout()
  const [productPrototype, setProductPrototype] = useState({
    ...product,
    ...(product.uuid
      ? cart.find(o => o.uuid === product.uuid)
      : {
          uuid: uuidv4(),
          quantity: 1,
          activeAddons: [],
          activeVariant: product.variants ? product.variants[0] : null
        })
  })
  const totalAmount = useMemo(() => {
    const { activeAddons, activeVariant, quantity } = productPrototype
    const addonsPrice = activeAddons.reduce((acc, addon) => acc + addon.price.amount, 0)
    const variantPrice = activeVariant ? activeVariant.price.amount : product.price.amount
    return (addonsPrice + variantPrice) * quantity
  }, [productPrototype])

  const { activeAddons, activeVariant, quantity } = productPrototype

  const handleQtyChange = value => {
    setProductPrototype({
      ...productPrototype,
      quantity: value
    })
  }

  const handleSubmit = () => {
    if (mode === 'ADD') {
      addToCart({
        ...productPrototype,
        totalAmount
      })
    } else {
      updateCartItem({
        ...productPrototype,
        totalAmount
      })
    }
    onClose()
  }

  const handleAddonChange = ({ addon, value }) => {
    if (value) {
      setProductPrototype({
        ...productPrototype,
        activeAddons: [...activeAddons, addon]
      })
    } else {
      setProductPrototype({
        ...productPrototype,
        activeAddons: activeAddons.filter(o => o.id !== addon.id)
      })
    }
  }

  const handleVariantChange = ({ variant, value }) => {
    if (value) {
      setProductPrototype({
        ...productPrototype,
        activeVariant: variant
      })
    } else {
      return
    }
  }

  return (
    <Modal title={product.variants && product.variants.length > 0 && title} onClose={onClose}>
      {/* Main (without variants) */}
      {(!product.variants || product.variants.length === 0) && (
        <ProductModalMain>
          <ProductModalMainImage>
            <LazyImage src={product.imageUrl} />
          </ProductModalMainImage>
          <ProductModalMainContent>
            <ProductModalMainContentTitle>{product.name}</ProductModalMainContentTitle>
            {product.additionalInfo && <ProductModalMainContentCaption>{product.additionalInfo}</ProductModalMainContentCaption>}
          </ProductModalMainContent>
        </ProductModalMain>
      )}
      {/* Variants */}
      {product.variants && product.variants.length > 0 && (
        <ProductModalVariants>
          {product.variants.map(variant => (
            <ProductVariant
              key={variant.id}
              {...variant}
              active={activeVariant.id === variant.id}
              onChange={payload => handleVariantChange({ variant, value: payload })}
            />
          ))}
        </ProductModalVariants>
      )}
      {/* Addons */}
      {product.additions && product.additions.length > 0 && (
        <ProductModalAddonsWrapper horizontal={true}>
          <ProductModalAddonsHeader>
            <ProductModalAddonsTitle>{t('chooseAddons')}</ProductModalAddonsTitle>
          </ProductModalAddonsHeader>
          <ProductModalAddonsScroll horizontal={true} contentContainerStyle={{ paddingHorizontal: 50 }}>
            <ProductModalAddonsList>
              {product.additions.map(addon => (
                <ProductAddon
                  key={addon.id}
                  {...addon}
                  active={activeAddons.find(o => o.id === addon.id)}
                  uuid={productPrototype.uuid}
                  onChange={payload => handleAddonChange({ addon, value: payload })}
                />
              ))}
            </ProductModalAddonsList>
          </ProductModalAddonsScroll>
        </ProductModalAddonsWrapper>
      )}
      {/* Footer */}
      <ProductModalFooter>
        <Button variant="outline-dark" onClick={onClose}>
          {t('cancel')}
        </Button>
        <ProductModalFooterTotal>
          {totalAmount.toFixed(2)} {CURRENCY}
        </ProductModalFooterTotal>
        <InputQty value={quantity} onChange={handleQtyChange} />
        <Button onClick={handleSubmit}>{mode === 'ADD' ? t('addToCart') : t('saveChanges')}</Button>
      </ProductModalFooter>
    </Modal>
  )
}

export default withTranslation()(ProductModal)
